import { useCallback, useMemo, useState } from 'react';
import type { RoiId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { StructureSet } from '~/domain/patient/structureset';
import {
    CopyTargetPlan,
    indexTargetNames,
    planCopyTarget,
    planCopyTargets,
} from '~/domain/services/structureSetService/copyTargetPlan';
import { RoiFilter } from './types';
import { TYPE_SORT_ORDER, INTERPRETED_TYPE_GROUPS } from './constants';

/** The structures whose plan satisfies `predicate`. */
function roiIdsWhere(
    plans: ReadonlyMap<RoiId, CopyTargetPlan>,
    predicate: (plan: CopyTargetPlan) => boolean
): Set<RoiId> {
    return new Set(
        [...plans].filter(([, plan]) => predicate(plan)).map(([roiId]) => roiId)
    );
}

/**
 * Filter + selection state for the Copy Structures dialog.
 *
 *   selectedRois = (filteredRois \ disabledRoiIds) \ uncheckedRois
 *
 * When `scopedRoiIds` is provided the visible/selectable set is restricted
 * to those ROIs (intersected with the user filters), used by single-ROI copy.
 *
 * When `initialSelectedRoiIds` is provided only those ROIs start checked
 * (all others begin unchecked); an omitted value preserves select-all.
 */
export function useRoiFilterAndSelection(
    sourceStructureSet: StructureSetReadModel,
    activeImageTargetSs: StructureSet | null,
    overwriteExisting: boolean,
    scopedRoiIds: ReadonlySet<RoiId> | undefined = undefined,
    initialSelectedRoiIds?: ReadonlySet<RoiId>
) {
    const [filters, setFilters] = useState<RoiFilter>({
        visibleOnly: false,
        approvedOnly: false,
        interpretedTypes: new Set(),
        searchText: '',
    });

    // Source ROIs the caller did NOT pre-select start unchecked. An omitted
    // pre-selection yields the empty set (today's select-all behavior).
    const initialUncheckedRois = useMemo(() => {
        const unchecked = new Set<RoiId>();
        if (!initialSelectedRoiIds) {
            return unchecked;
        }
        for (const roi of sourceStructureSet.roiArray) {
            if (!initialSelectedRoiIds.has(roi.roiId)) {
                unchecked.add(roi.roiId);
            }
        }
        return unchecked;
        // Pre-selection is captured once on mount; later prop identity changes
        // must not silently reset the user's checkbox edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persists across filter / target changes; cleared only by clearFilters
    // or by re-checking the row.
    const [uncheckedRois, setUncheckedRois] = useState<Set<RoiId>>(
        () => new Set(initialUncheckedRois)
    );

    const presentTypeGroups = useMemo(() => {
        const sourceTypes = new Set(
            sourceStructureSet.roiArray.map(
                (r) => r.interpretedType as RoiInterpretedType
            )
        );
        return INTERPRETED_TYPE_GROUPS.filter((group) =>
            group.types.some((t) => sourceTypes.has(t))
        );
    }, [sourceStructureSet.roiArray]);

    // What the copy does with each source ROI on its own, by the rule the
    // copy applies. A row the copy would skip is disabled.
    const rowPlans = useMemo(() => {
        const plans = new Map<RoiId, CopyTargetPlan>();
        if (!activeImageTargetSs) {
            return plans;
        }
        const targetIndex = indexTargetNames(
            activeImageTargetSs.dicomStructureSet.rois.values()
        );
        for (const roi of sourceStructureSet.roiArray) {
            plans.set(
                roi.roiId,
                planCopyTarget(roi.name, targetIndex, overwriteExisting)
            );
        }
        return plans;
    }, [sourceStructureSet.roiArray, activeImageTargetSs, overwriteExisting]);

    // ROIs whose name the target already holds. Always computed, for the warnings.
    const conflictingRoiIds = useMemo(
        () => roiIdsWhere(rowPlans, (plan) => plan.kind !== 'create'),
        [rowPlans]
    );

    // ROIs the user cannot select, given the overwrite toggle.
    const disabledRoiIds = useMemo(
        () => roiIdsWhere(rowPlans, (plan) => plan.kind === 'skip'),
        [rowPlans]
    );

    const filteredRois = useMemo(() => {
        return sourceStructureSet.roiArray
            .filter((roi) => {
                if (scopedRoiIds && !scopedRoiIds.has(roi.roiId)) {
                    return false;
                }
                if (filters.visibleOnly && !roi.visible) {
                    return false;
                }
                if (
                    filters.approvedOnly &&
                    roi.approvalStatus !== DICOMApprovalStatus.APPROVED
                ) {
                    return false;
                }
                if (
                    filters.interpretedTypes.size > 0 &&
                    !filters.interpretedTypes.has(
                        roi.interpretedType as RoiInterpretedType
                    )
                ) {
                    return false;
                }
                if (
                    filters.searchText &&
                    !roi.name
                        .toLowerCase()
                        .includes(filters.searchText.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                const orderA = TYPE_SORT_ORDER[a.interpretedType] ?? 99;
                const orderB = TYPE_SORT_ORDER[b.interpretedType] ?? 99;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.name.localeCompare(b.name);
            });
    }, [sourceStructureSet.roiArray, filters, scopedRoiIds]);

    const filteredRoiIds = useMemo(
        () =>
            new Set(
                filteredRois
                    .filter((r) => !disabledRoiIds.has(r.roiId))
                    .map((r) => r.roiId)
            ),
        [filteredRois, disabledRoiIds]
    );

    const selectedRois = useMemo(() => {
        const result = new Set<RoiId>();
        for (const n of filteredRoiIds) {
            if (!uncheckedRois.has(n)) {
                result.add(n);
            }
        }
        return result;
    }, [filteredRoiIds, uncheckedRois]);

    // Each row's plan, with the selected rows planned together in the order
    // the copy takes them, so the row of a selected ROI that repeats an
    // earlier selected name says it is left out.
    const copyPlans = useMemo((): ReadonlyMap<RoiId, CopyTargetPlan> => {
        if (!activeImageTargetSs) {
            return rowPlans;
        }
        const roisById = new Map(
            sourceStructureSet.roiArray.map((roi) => [roi.roiId, roi])
        );
        const selected = [...selectedRois].flatMap((roiId) => {
            const roi = roisById.get(roiId);
            return roi ? [roi] : [];
        });
        return new Map([
            ...rowPlans,
            ...planCopyTargets(
                selected,
                activeImageTargetSs.dicomStructureSet.rois.values(),
                overwriteExisting
            ),
        ]);
    }, [
        rowPlans,
        selectedRois,
        sourceStructureSet.roiArray,
        activeImageTargetSs,
        overwriteExisting,
    ]);

    const toggleRoi = useCallback(
        (roiId: RoiId) => {
            if (disabledRoiIds.has(roiId)) {
                return;
            }
            setUncheckedRois((prev) => {
                const next = new Set(prev);
                if (next.has(roiId)) {
                    next.delete(roiId);
                } else {
                    next.add(roiId);
                }
                return next;
            });
        },
        [disabledRoiIds]
    );

    const allFilteredSelected = useMemo(
        () =>
            filteredRoiIds.size > 0 &&
            [...filteredRoiIds].every((n) => selectedRois.has(n)),
        [filteredRoiIds, selectedRois]
    );

    const someFilteredSelected = useMemo(
        () => [...filteredRoiIds].some((n) => selectedRois.has(n)),
        [filteredRoiIds, selectedRois]
    );

    const toggleAllFiltered = useCallback(() => {
        setUncheckedRois((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                // Deselect every currently-filtered eligible ROI.
                filteredRoiIds.forEach((n) => next.add(n));
            } else {
                // Re-select every currently-filtered eligible ROI.
                filteredRoiIds.forEach((n) => next.delete(n));
            }
            return next;
        });
    }, [allFilteredSelected, filteredRoiIds]);

    const toggleFilter = useCallback((key: 'visibleOnly' | 'approvedOnly') => {
        setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const toggleTypeGroup = useCallback(
        (types: readonly RoiInterpretedType[]) => {
            setFilters((prev) => {
                const next = new Set(prev.interpretedTypes);
                const allActive = types.every((t) => next.has(t));
                if (allActive) {
                    types.forEach((t) => next.delete(t));
                } else {
                    types.forEach((t) => next.add(t));
                }
                return { ...prev, interpretedTypes: next };
            });
        },
        []
    );

    const isGroupActive = useCallback(
        (types: readonly RoiInterpretedType[]) =>
            types.every((t) => filters.interpretedTypes.has(t)),
        [filters.interpretedTypes]
    );

    const clearFilters = useCallback(() => {
        setFilters({
            visibleOnly: false,
            approvedOnly: false,
            interpretedTypes: new Set(),
            searchText: '',
        });
        // Restore the initial pre-selection, not select-all: clearing filters
        // must never silently widen the copy scope.
        setUncheckedRois(new Set(initialUncheckedRois));
    }, [initialUncheckedRois]);

    const updateSearchText = useCallback((searchText: string) => {
        setFilters((prev) => ({ ...prev, searchText }));
    }, []);

    const hasActiveFilters =
        filters.visibleOnly ||
        filters.approvedOnly ||
        filters.interpretedTypes.size > 0 ||
        filters.searchText.length > 0;

    return {
        selectedRois,
        filters,
        filteredRois,
        presentTypeGroups,
        conflictingRoiIds,
        disabledRoiIds,
        copyPlans,
        allFilteredSelected,
        someFilteredSelected,
        hasActiveFilters,
        toggleRoi,
        toggleAllFiltered,
        toggleFilter,
        toggleTypeGroup,
        isGroupActive,
        clearFilters,
        updateSearchText,
    };
}
